import { React, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router";

import {
  get_channel_video,
  delete_channel_video_comment,
  report_channel_video_comment,
  add_channel_video_comment,
} from "../../../redux/features/ktkSlice";

import ktkRestrictService from "../../../services/ktkRestrictService";

import { Button } from "../../commonComponents/Buttons";
import VideoViewer from "../../commonComponents/VideoViewer/VideoViewer";

import { NoAvatar, ReportFlag, ReportedFlag, DeleteButton, ReplyButton, ReplyButton2, Shine, LikedIcon, ViewsIcon, UnlikedIcon} from "../../../assets";

import ktkService from "../../../services/ktkService";



function ChildVideoView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();

  const userInfo = useSelector((state) => state?.user?.userInfo);
  const [channelVideo, setChannelVideo] = useState();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [allowedWords, setAllowedWords] = useState([]);
  const [isCommentInvalid, setIsCommentInvalid] = useState(false);
  const [highlightedText, setHighlightedText] = useState("");
  const [showAllComments, setShowAllComments] = useState(false);
  const [replyingToId, setReplyingToId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [validatingType, setValidatingType] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalAction, setModalAction] = useState(null); // 'delete' or 'report'
  const [modalCommentId, setModalCommentId] = useState(null);

  
  useEffect(() => {
    fetch('/allowed_words.txt')
      .then(res => res.text())
      .then(text => {
        const words = text.split(/\s+/).map(w => w.toLowerCase());
        setAllowedWords(words);
      })
      .catch(err => console.error("Failed to load allowed words:", err));
  }, []);

  useEffect(() => {
    if (!channelVideo) {
      dispatch(get_channel_video({
        userId:    userInfo.id,        // only used in manager flow
        channelId: params.channelId,
        videoId:   params.videoId,
      }))
      .unwrap()
      .then(data => {
        setChannelVideo(data);
        setComments(data.comments || []);
        // …
      });
    }
  }, [channelVideo, dispatch, params, userInfo]);

  function restrictHandler() {
    ktkRestrictService
      .postRestrictChannelVideo(
        params?.userId,
        params?.channelId,
        params?.videoId
      )
      .then((result) => {
        let tempArray = { ...channelVideo };
        tempArray = {
          ...tempArray,
          restricted: result?.data?.result?.restricted,
        };

        setChannelVideo(tempArray);
      });
  }

  function renderOwnerInfo() {
    if (channelVideo && channelVideo?.channel?.age) {
      return `${channelVideo?.channel?.owner_name}, ${channelVideo?.channel?.age} ${t("years")}`;
    } else if (channelVideo && !channelVideo?.channel?.age) {
      return `${channelVideo?.channel?.owner_name}, ? ${t("years")}`;
    }
  }

  function handleDeleteComment(commentId) {
    setModalAction('delete');
    setModalCommentId(commentId);
    setModalVisible(true);
  }
  
  function handleReportComment(commentId) {
    setModalAction('report');
    setModalCommentId(commentId);
    setModalVisible(true);
  }
  

  function confirmModalAction() {
    const channelId = channelVideo.channel.id;
    const videoId = channelVideo.id;
  
    if (modalAction === 'delete') {
      dispatch(delete_channel_video_comment({ channelId, videoId, commentId: modalCommentId }))
        .unwrap()
        .then(() => {
          setComments(prev => prev.filter(c => c.id !== modalCommentId));
          setModalVisible(false);
        });
    } else if (modalAction === 'report') {
      dispatch(report_channel_video_comment({ channelId, videoId, commentId: modalCommentId }))
        .then(() => {
          window.location.reload();
        });
    }
  }
  
  
function handleTextChange(e, type) {
	const input = e.target.value;
	if (input.length > 1000) return;

	if (type === "comment") {
		setNewComment(input);
	} else if (type === "reply") {
		setReplyText(input);
	}

	setValidatingType(type);

	const punctuationAndNumberRegex = /[0-9.,!+_=*|^%$#`~@?()&\\/<>[\]{}:;"'“‘…-]+/g;
	const hasLetters = /[a-zA-Z]/;

	const words = input.split(/(\s+)/); // keep whitespace tokens

	const isAllowedToken = (original) => {
		if (!original || /^\s+$/.test(original)) return true; // whitespace is fine

		const orig = String(original).toLowerCase();

		// Allow tokens that have NO letters (numbers/symbols only), e.g., "1", "+", "=", ":)"
		if (!hasLetters.test(orig)) return true;

		// Otherwise (contains letters), check against allowed_words.txt
		const cleaned = orig.replace(punctuationAndNumberRegex, "");
		const inAllowed = (w) =>
			allowedWords.includes(w) ||
			(w.endsWith("s") && allowedWords.includes(w.slice(0, -1))); // simple plural → singular

		// Accept if either the raw or cleaned form is in the list
		return inAllowed(orig) || (cleaned ? inAllowed(cleaned) : false);
	};

	const invalidTokens = words.filter(
		(token) => token && !/^\s+$/.test(token) && !isAllowedToken(token)
	);

	setHighlightedText(invalidTokens.join(" "));
	setIsCommentInvalid(invalidTokens.length > 0);
}

  function handleAddComment() {
    if (!newComment.trim()) return;
  
    const data = {
      channelId: channelVideo.channel.id,
      videoId: channelVideo.id,
      text: newComment,
    };
  
    dispatch(add_channel_video_comment(data))
      .unwrap()
      .then((newCommentObj) => {
        setComments((prev) => [newCommentObj, ...prev]);
        setNewComment("");
      })
      .catch((error) => {
        console.error("Failed to add comment:", error);
      });
  }

  function handlePostReply(parentId) {
    if (!replyText.trim()) return;
    const data = {
      channelId: channelVideo.channel.id,
      videoId: channelVideo.id,
      text: replyText,
      parent: parentId,  // ✅ Correct: just the parent comment's id
    };
  
    dispatch(add_channel_video_comment(data))
      .unwrap()
      .then((newReply) => {
        // Make sure the new reply has the correct parent set
        newReply.parent = parentId;  // ✅ Force parent field if needed
        setComments((prev) => [newReply, ...prev]);  // ✅ Just add to the list
        setReplyText("");
        setReplyingToId(null);
      })
      .catch((error) => {
        console.error("Failed to add reply:", error);
      });
  }
  
  function likeHandler() {
    // call your like API
    ktkService
      .addVideoLike(params.channelId, channelVideo.id)
      .then((result) => {
        // merge in the new like count and liked flag
        setChannelVideo((prev) => ({
          ...prev,
          likes: result.data.result.counter,
          additional_info: {
            ...prev.additional_info,
            liked: result.data.result.liked,
          },
        }));
      })
      .catch(console.error);
  }

  return (
    <>
      <main className="ktkBg">
        {modalVisible && (
          <div className="modalOverlay">
            <div className="modalBox">
              <p className="mainText">
                {modalAction === 'delete'
                  ? "Are you sure you want to delete this comment?"
                  : "Are you sure you want to report this comment?"}
              </p>
              <div className="modalActions">
                <button className="cancelButton" onClick={() => setModalVisible(false)}><p>Cancel</p></button>
                <button className="yesButton" onClick={confirmModalAction}><p><img className="shine" src={Shine} alt=""/>
                Yes
                </p></button>
              </div>
            </div>
          </div>
        )}

        <Button
          label={t("back")}
          iconClassName="hasIcon--chevron-left"
          viewType="backButton"
          onClick={() => navigate(-1)}
        />
        {userInfo?.account_level === "manager" && (
          <Button
            label={
              channelVideo && channelVideo?.restricted ? t("allow") : t("restrict")
            }
            iconClassName={
              channelVideo && channelVideo?.restricted
                ? "hasIcon--check-circle"
                : "hasIcon--disable"
            }
            customClassName={
              channelVideo && channelVideo?.restricted ? "allowBtn" : ""
            }
            viewType="restrictButton"
            onClick={() => restrictHandler()}
          />
        )}
        <section className="withAsideArticle">
          <aside>
            <section className="channelInfo">
              <section className="mainInfo">
                <img
                  className="userImage"
                  src={
                    channelVideo?.channel?.avatar
                      ? channelVideo?.channel?.avatar
                      : NoAvatar
                  }
                  alt={channelVideo?.channel?.owner_name}
                />

                <div className="userInfo">
                  <div className="textInfo">
                    <p className="ownerInfo">{renderOwnerInfo()}</p>
                    <p className="channelTitle">
                      {channelVideo?.channel?.title}
                    </p>
                  </div>
                </div>
              </section>

              <p className="channelDescription">
                {channelVideo?.channel?.description}
              </p>
            </section>
          </aside>

          <section className="videoContent">
            <VideoViewer
              handlers="ktk"
              user={userInfo && userInfo?.account_level === "manager" && 'manager'}
              videoUrl={channelVideo?.video_link}
              channelId={channelVideo?.channel?.id}
              videoId={channelVideo?.id}
              title={channelVideo?.title}
            />
            {!userInfo?.disable_comments && !userInfo?.ban_commenting && (
              <section className="commentSection">
                <div className="mainTitle">
                  <div className="information">
              
                    <p style={{marginLeft:"10px", color:"#ff66c4"}}><img style={{width:"20px", marginRight:"6px"}} className="activityIcon" onClick={likeHandler} src={ channelVideo?.additional_info?.liked ? LikedIcon : UnlikedIcon } alt="likes"/>{channelVideo?.likes}</p>
                
                    <p style={{marginLeft:"10px", color:"#21c063"}}><img src={ViewsIcon} style={{width:"30px", marginRight:"6px"}} alt=""/>{channelVideo?.views}</p>
                  </div>
                  Comments
                </div>
                {!userInfo?.ban_commenting && (
                  <div className="addComment">
                  <textarea
                    placeholder={t("Write a comment")}
                    value={newComment}
                    onChange={(e) => handleTextChange(e, "comment")}
                  />

                  {validatingType === "comment" && isCommentInvalid && (
                    <>
                      <p style={{color:"red"}}>"{highlightedText}"</p>
                      <p style={{ color: 'red', margin: '0.5rem 0' }}>Unavailable word(s).</p>
                    </>
                  )}

                  <button onClick={handleAddComment} disabled={isCommentInvalid} className="simple-button" ><img className="shine" src={Shine} alt=""/>Post Comment</button>
                
                  </div>
                )}               
                <ul className="commentList">
                  {(showAllComments ? comments.slice(0, 100) : comments.slice(0, 10))
                  .filter(comment => comment.parent === null)
                  .map((comment) => (
                    <>
                      {/* Parent comment */}
                      <li className={comment.user_id === channelVideo?.channel?.user_id ? "creators-comment" : comment.user_id === userInfo?.id ? "own-comment" : ""}>
                        {comment?.channel?.id ? (
                          <a href={`/kids-teach-kids/channel/${comment?.channel?.id}`}>
                          	<img
                          		src={comment?.channel?.channel_avatar || NoAvatar}
                          		alt=""
                          		className="avatar"
                          	/>
                          </a>
                        ) : (
                          <img
                          	src={comment?.channel?.channel_avatar || NoAvatar}
                          	alt=""
                          	className="avatar"
                          />
                        )}


                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>
                            <strong>{comment.channel?.title || comment.user_name}</strong>: {comment.text}
                          </span>
                        </div>

                        {/* Reply form */}
                        {replyingToId === comment.id && (
                          <div className="addReply">
                            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                              <button
                                onClick={() => setReplyingToId(null)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  fontSize: "30px",
                                  fontWeight: "bold",
                                  cursor: "pointer",
                                }}
                                aria-label="Close reply box"
                              >
                                ×
                              </button>
                            </div>
                            <textarea
                              placeholder="Write a reply..."
                              value={replyText}
                              onChange={(e) => handleTextChange(e, "reply")}
                            />

                            {validatingType === "reply" && isCommentInvalid && (
                              <>
                                <p style={{ color: comment.user_id === channelVideo?.channel?.user_id ? "yellow" : "red" }}>"{highlightedText}"</p>
                                <p style={{ color: comment.user_id === channelVideo?.channel?.user_id ? "yellow" : "red", margin: '0.5rem 0' }}>Unavailable word(s).</p>
                              </>
                            )}
                            <button onClick={() => handlePostReply(comment.id)} disabled={isCommentInvalid} className="simple-button" ><img className="shine" src={Shine} alt=""/>Post Reply</button>
                          </div>
                        )}


                        <div className="commentActions" style={{backgroundColor: comment.user_id === channelVideo?.channel?.user_id ? "#8c52ff" : comment.user_id === userInfo?.id ? "#eadac2" : "#f7f4e6"}}>
                          <img
                            onClick={() => setReplyingToId(comment.id)}
                            src={comment.user_id === channelVideo?.channel?.user_id ? ReplyButton2 : ReplyButton}
                            className="reply-button"
                            alt=""
                          />
                          {comment.user_id !== userInfo?.id && userInfo?.id !== channelVideo?.channel?.user_id && (
                            <img
                              className="reportFlag"
                              onClick={() => {
                                if (!comment.reported) {
                                  handleReportComment(comment.id);
                                }
                              }}
                              src={comment.reported ? ReportedFlag : ReportFlag}
                              alt={t("videos")}
                            />
                          )}
                          { (comment.user_id === userInfo?.id || 
                             userInfo?.id === channelVideo?.channel?.user_id) && (
                              <img
                                onClick={() => handleDeleteComment(comment.id)}
                                src={DeleteButton}
                                className="delete-button"
                                alt=""
                              />
                          )}

                        </div>
                      </li>
                        
                      {/* Replies for this parent */}
                      {comments
                      	.filter(reply => reply.parent === comment.id)
                      	.slice() // clone to avoid mutating the original array
                      	.reverse() // display in reverse order
                      	.map((reply) => (
                      		<li
                      			key={reply.id}
                      			style={{ marginLeft: '110px' }}
                      			className={reply.user_id === channelVideo?.channel?.user_id ? "creators-comment" : reply.user_id === userInfo?.id ? "own-comment" : ""}
                      		>
                      			{reply.channel?.id ? (
                      				<a href={`/kids-teach-kids/channel/${reply.channel.id}`}>
                      					<img
                      						src={reply.channel?.channel_avatar || NoAvatar}
                      						alt={reply.user_name}
                      						className="avatar"
                      					/>
                      				</a>
                      			) : (
                      				<img
                      					src={reply.channel?.channel_avatar || NoAvatar}
                      					alt={reply.user_name}
                      					className="avatar"
                      				/>
                      			)}

                      			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      				<span>
                      					<strong>{reply.user_name}</strong>: {reply.text}
                      				</span>
                      			</div>
                          
                      			<div
                      				className="commentActions"
                      				style={{ backgroundColor: reply.user_id === channelVideo?.channel?.user_id ? "#8c52ff" : reply.user_id === userInfo?.id ? "#eadac2" : "#f7f4e6" }}
                      			>
                      				{reply.user_id !== userInfo?.id && (
                      					<img
                      						className="reportFlag"
                      						onClick={() => {
                      							if (!reply.reported) {
                      								handleReportComment(reply.id);
                      							}
                      						}}
                      						src={reply.reported ? ReportedFlag : ReportFlag}
                      						alt={t("videos")}
                      					/>
                      				)}

                      				{(reply.user_id === userInfo?.id ||
                      					userInfo?.id === channelVideo?.channel?.user_id) && (
                      					<img
                      						onClick={() => handleDeleteComment(reply.id)}
                      						src={DeleteButton}
                      						className="delete-button"
                      						alt=""
                      					/>
                      				)}
                      			</div>
                      		</li>
                      	))}

                    </>
                  ))}

                </ul>
          
                {comments.length > 10 && (
                  <button
                    onClick={() => setShowAllComments((prev) => !prev)}
                    style={{ marginTop: '0.5rem' }}
                  >
                    {showAllComments ? "Show Less" : "Show All"}
                  </button>
                )}
              </section>
            )}
          </section>
        </section>
      </main>
      {userInfo &&
      userInfo?.account_level === "manager" &&
      channelVideo?.restricted && (
        <div className="restrictedMessageVideo">
          {t("restrictedVideoTo")} {channelVideo?.kid_name}
        </div>
      )}
    </>
  );
}

export default ChildVideoView;
